// src/utils/file.utils.ts
/**
 * Extracts original filename by removing encryption-related extensions
 */
export function extractOriginalFileName(encryptedFileName: string): string {
  return encryptedFileName.replace(/\.(encrypted|key\.encrypted)$/, '');
}

/**
 * Extracts original file type from encrypted filename
 */
export function extractOriginalFileType(encryptedFileName: string): string {
  const originalFileName = extractOriginalFileName(encryptedFileName);
  return getFileTypeFromExtension(originalFileName);
}

/**
 * Determines MIME type from file extension
 */
export function getFileTypeFromExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return 'application/octet-stream';
  }

  const extension = fileName.substring(lastDotIndex + 1).toLowerCase();

  // Common MIME type mappings
  const mimeTypeMap: Record<string, string> = {
    // Text
    'txt': 'text/plain',
    'csv': 'text/csv',
    'html': 'text/html',
    'css': 'text/css',
    'js': 'application/javascript',
    'json': 'application/json',
    'xml': 'application/xml',

    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'bmp': 'image/bmp',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'ico': 'image/x-icon',

    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'ogg': 'audio/ogg',

    // Video
    'mp4': 'video/mp4',
    'avi': 'video/x-msvideo',
    'mov': 'video/quicktime',
    'webm': 'video/webm',

    // Archives
    'zip': 'application/zip',
    'rar': 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    'tar': 'application/x-tar',
    'gz': 'application/gzip'
  };

  return mimeTypeMap[extension] ?? 'application/octet-stream';
}

/**
 * Generates encrypted filename from original
 */
export function generateEncryptedFileName(originalFileName: string): string {
  return `${originalFileName}.encrypted`;
}

/**
 * Generates encrypted key filename from original
 */
export function generateEncryptedKeyFileName(originalFileName: string): string {
  return `${originalFileName}.key.encrypted`;
}
